import type { ElementType, ReactNode } from "react";
import { twJoin } from "tailwind-merge";

import type { DesignSystemAs, DesignSystemElementProps, DesignSystemRef } from "./contracts/dom";
import type { ContainerBreakpoint, ContainerResponsiveValue } from "./contracts/responsive";
import { resolveContainerClasses } from "./foundations/responsiveClasses";
import { resolveGapClasses, type Space } from "./foundations/space";

export type TileColumnCount = 1 | 2 | 3 | 4;

const TILE_COLUMN_CLASSES: Record<TileColumnCount, string> = {
  1: "grid-cols-1",
  2: "grid-cols-2",
  3: "grid-cols-3",
  4: "grid-cols-4",
};

const TILE_CONTAINER_COLUMN_CLASSES: Record<ContainerBreakpoint, Record<TileColumnCount, string>> = {
  xs: { 1: "@xs:grid-cols-1", 2: "@xs:grid-cols-2", 3: "@xs:grid-cols-3", 4: "@xs:grid-cols-4" },
  sm: { 1: "@sm:grid-cols-1", 2: "@sm:grid-cols-2", 3: "@sm:grid-cols-3", 4: "@sm:grid-cols-4" },
  md: { 1: "@md:grid-cols-1", 2: "@md:grid-cols-2", 3: "@md:grid-cols-3", 4: "@md:grid-cols-4" },
  lg: { 1: "@lg:grid-cols-1", 2: "@lg:grid-cols-2", 3: "@lg:grid-cols-3", 4: "@lg:grid-cols-4" },
  xl: { 1: "@xl:grid-cols-1", 2: "@xl:grid-cols-2", 3: "@xl:grid-cols-3", 4: "@xl:grid-cols-4" },
};

type TilesElement = "div" | "ul" | "ol";

export type TilesProps<TElement extends TilesElement = "div"> = {
  children: ReactNode;
  columns: ContainerResponsiveValue<TileColumnCount>;
  gap?: ContainerResponsiveValue<Space>;
} & DesignSystemAs<TElement> &
  DesignSystemElementProps<TElement> &
  DesignSystemRef<HTMLElementTagNameMap[TElement]>;

/** Equal-width cells. Responsive columns and gaps resolve against an explicit Container ancestor. */
export function Tiles<TElement extends TilesElement = "div">({
  as,
  children,
  columns,
  gap,
  ref,
  ...nativeProps
}: TilesProps<TElement>): ReactNode {
  const Element = (as ?? "div") as ElementType;

  return (
    <Element
      {...nativeProps}
      ref={ref}
      className={twJoin(
        "grid",
        resolveContainerClasses(columns, TILE_COLUMN_CLASSES, TILE_CONTAINER_COLUMN_CLASSES),
        resolveGapClasses(gap),
      )}
    >
      {children}
    </Element>
  );
}
